import React from "react";
import { useClinic } from "../../context/ClinicContext";
import { db } from "../../firebase";
import {
  collection,
  doc,
  getDocs,
  setDoc,
  addDoc,
  deleteDoc
} from "../../access/firestore";
import { consentTemplates } from "../../consentTemplates";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import {
    TextStyle,
    FontSize
} from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import FontFamily from "@tiptap/extension-font-family";
import { Table } from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import { Extension } from "@tiptap/core";
import {
    Bold,
    Italic,
    Underline as UnderlineIcon,
    Strikethrough,
    AlignLeft,
    AlignCenter,
    AlignRight,
    List,
    ListOrdered,
    Undo2,
    Redo2,
    Eraser,
    AlignJustify,
    IndentIncrease,
    IndentDecrease,
    Minus,
    Table2,
    Trash2
} from "lucide-react";

export default function ConsentimientosSettings() {
  const { clinicId } = useClinic();

  const [consentimientos, setConsentimientos] = React.useState([]);

  const [mostrarFormulario, setMostrarFormulario] =
    React.useState(false);

  const [nombreNuevo, setNombreNuevo] =
    React.useState("");

  const [textoNuevo, setTextoNuevo] =
    React.useState("");

  const LineHeight = Extension.create({
    name: "lineHeight",

    addGlobalAttributes() {
        return [
            {
                types: ["paragraph", "heading"],
                attributes: {
                    lineHeight: {
                        default: null,
                        parseHTML: element =>
                            element.style.lineHeight || null,
                        renderHTML: attributes => {
                            if (!attributes.lineHeight) {
                                return {};
                            }

                            return {
                                style: `line-height: ${attributes.lineHeight}`
                            };
                        }
                    }
                }
            }
        ];
    }
}); 

  const editor = useEditor({
    extensions: [
    StarterKit,
    Underline,
    TextAlign.configure({
        types: ["heading", "paragraph"]
    }),
    TextStyle,
    Color,
    Highlight,
    FontFamily,
    FontSize.configure({
        types: ["textStyle"]
    }),
    LineHeight,
    Table.configure({
        resizable: true
    }),
    TableRow,
    TableHeader,
    TableCell
  ],
    content: textoNuevo,
    onUpdate: ({ editor }) => {
      setTextoNuevo(editor.getHTML());
    }
  });  

  const [, setEditorUpdate] = React.useState(0);

    React.useEffect(() => {
        if (!editor) {
            return;
        }

        const actualizarToolbar = () => {
            setEditorUpdate(valor => valor + 1);
        };

        editor.on("transaction", actualizarToolbar);

        return () => {
            editor.off("transaction", actualizarToolbar);
        };
  }, [editor]);

  const [consentimientoEditando, setConsentimientoEditando] =
    React.useState(null);

  const [consentimientoAEliminar, setConsentimientoAEliminar] =
    React.useState(null);

  const [confirmacion, setConfirmacion] =
    React.useState(null);

  React.useEffect(() => {
    if (!clinicId) return;

    const cargarConfiguraciones = async () => {
      const ref = collection(
        db,
        "clinics",
        clinicId,
        "consentimientosConfiguracion"
      );

      const snapshot = await getDocs(ref);

      const configuracionesMap = {};

      snapshot.docs.forEach(doc => {
        configuracionesMap[doc.id] = doc.data();
      });

      const nombresConsentimientos = {
        exodoncia: "Extracción dental",
        endodoncia: "Endodoncia",
        ortodoncia: "Ortodoncia",
        implantes: "Implantes dentales",
        operatoria: "Operatoria dental",
        periodoncia: "Periodoncia",
        odontologiaIntegral: "Odontología integral",
        prostodoncia: "Prostodoncia",
        profilaxis: "Profilaxis dental",
        radiografias: "Radiografías",
        anestesia: "Anestesia"
      };

      const descripcionesConsentimientos = {
        exodoncia:
          "Consentimiento informado para procedimientos de extracción dental.",
        endodoncia:
          "Consentimiento informado para tratamientos de conductos radiculares.",
        ortodoncia:
          "Consentimiento informado para tratamiento de ortodoncia.",
        implantes:
          "Consentimiento informado para procedimientos de colocación de implantes dentales.",
        operatoria:
          "Consentimiento informado para procedimientos de operatoria dental.",
        periodoncia:
          "Consentimiento informado para tratamientos periodontales.",
        odontologiaIntegral:
          "Consentimiento informado para atención odontológica integral.",
        prostodoncia:
          "Consentimiento informado para tratamientos de rehabilitación protésica.",
        profilaxis:
          "Consentimiento informado para procedimientos de profilaxis dental.",
        radiografias:
          "Consentimiento informado para la toma de estudios radiográficos.",
        anestesia:
          "Consentimiento informado para la aplicación de anestesia odontológica."
      };

      const listaPredeterminados =
        Object.keys(consentTemplates).map(tipo => ({
          id: tipo,
          tipo,
          nombre:
            configuracionesMap[tipo]?.nombre ||
            nombresConsentimientos[tipo] ||
            tipo,
          descripcion:
            descripcionesConsentimientos[tipo] ||
            "Consentimiento informado incluido con VexaDent.",
          activo:
            configuracionesMap[tipo]?.activo !== false,
          origen: "vexadent"
        }));

      const listaPersonalizados = snapshot.docs
        .filter(
          doc => doc.data().origen === "custom"
        )
        .map(doc => ({
          id: doc.id,
          tipo: doc.id,
          nombre:
            doc.data().nombre || "Sin nombre",
          descripcion:
            doc.data().descripcion ||
            "Consentimiento creado por este consultorio.",
          activo:
            doc.data().activo !== false,
          origen: "custom",
          textoPlantilla:
            doc.data().textoPlantilla || "",
          version:
            doc.data().version || 1
        }));

      setConsentimientos([
        ...listaPredeterminados,
        ...listaPersonalizados
      ]);
    };

    cargarConfiguraciones();
  }, [clinicId]);

  const cambiarEstado = async (id, activo) => {
    if (!clinicId) return;

    const scrollX = window.scrollX;
    const scrollY = window.scrollY;

    const nuevoEstado = !activo;

    setConsentimientos(prev =>
      prev.map(consentimiento =>
        consentimiento.id === id
          ? {
              ...consentimiento,
              activo: nuevoEstado
            }
          : consentimiento
      )
    );

    try {
      await setDoc(
        doc(
          db,
          "clinics",
          clinicId,
          "consentimientosConfiguracion",
          id
        ),
        {
          activo: nuevoEstado
        },
        {
          merge: true
        }
      );
    } catch (error) {
      console.error(
        "Error al cambiar estado del consentimiento:",
        error
      );

      setConsentimientos(prev =>
        prev.map(consentimiento =>
          consentimiento.id === id
            ? {
                ...consentimiento,
                activo
              }
            : consentimiento
        )
      );
    }

    requestAnimationFrame(() => {
      window.scrollTo(scrollX, scrollY);

      requestAnimationFrame(() => {
        window.scrollTo(scrollX, scrollY);
      });
    });
  };

  const abrirNuevoConsentimiento = () => {
    const contenidoInicial =
        "Yo, {{paciente}}, declaro que el profesional de la salud {{doctor}} ";

    setConsentimientoEditando(null);
    setNombreNuevo("");
    setTextoNuevo(contenidoInicial);

    if (editor) {
        editor.commands.setContent(contenidoInicial);
    }

    setMostrarFormulario(true);
  };

  const abrirEdicion = consentimiento => {
    const contenido =
        consentimiento.contenidoHtml ||
        consentimiento.textoPlantilla ||
        "";

    setConsentimientoEditando(consentimiento);
    setNombreNuevo(consentimiento.nombre || "");
    setTextoNuevo(contenido);

    if (editor) {
        editor.commands.setContent(contenido);
    }

    setMostrarFormulario(true);
  };

  const solicitarCancelarFormulario = () => {
    setConfirmacion({
      tipo: "cancelar",
      titulo: consentimientoEditando
        ? "¿Descartar cambios?"
        : "¿Cancelar creación?",
      mensaje: consentimientoEditando
        ? "Los cambios realizados en este consentimiento no se guardarán."
        : "El consentimiento que estás creando no se guardará.",
      textoBoton: consentimientoEditando
        ? "Sí, descartar cambios"
        : "Sí, cancelar",
      accion: () => {
        setConfirmacion(null);
        setMostrarFormulario(false);
        setConsentimientoEditando(null);
        setNombreNuevo("");
        setTextoNuevo("");
      }
    });
  };

  const insertarVariable = variable => {
    if (!editor) {
        return;
    }

    editor
        .chain()
        .focus()
        .insertContent(variable)
        .run();
  };


  const solicitarGuardarConsentimiento = () => {
    if (!nombreNuevo.trim()) {
      setConfirmacion({
        tipo: "error",
        titulo: "Falta el nombre",
        mensaje:
          "Escribe el nombre del consentimiento antes de continuar.",
        textoBoton: "Entendido",
        accion: () => {
          setConfirmacion(null);
        }
      });
      return;
    }

    if (!textoNuevo.trim()) {
      setConfirmacion({
        tipo: "error",
        titulo: "Falta el contenido",
        mensaje:
          "Escribe el texto del consentimiento antes de continuar.",
        textoBoton: "Entendido",
        accion: () => {
          setConfirmacion(null);
        }
      });
      return;
    }

    const nombreNormalizado =
      nombreNuevo.trim().toLowerCase();

    const nombreDuplicado =
      consentimientos.some(
        consentimiento =>
          consentimiento.origen === "custom" &&
          consentimiento.id !==
            consentimientoEditando?.id &&
          consentimiento.nombre
            ?.trim()
            .toLowerCase() ===
            nombreNormalizado
      );

    if (nombreDuplicado) {
      setConfirmacion({
        tipo: "error",
        titulo: "Nombre duplicado",
        mensaje:
          "Ya existe un consentimiento personalizado con ese nombre. Utiliza un nombre diferente.",
        textoBoton: "Entendido",
        accion: () => {
          setConfirmacion(null);
        }
      });

      return;
    }

    const tienePaciente =
      textoNuevo.includes("{{paciente}}");

    const tieneDoctor =
      textoNuevo.includes("{{doctor}}");

    if (!tienePaciente || !tieneDoctor) {
      const faltantes = [];

      if (!tienePaciente) {
        faltantes.push("{{paciente}}");
      }

      if (!tieneDoctor) {
        faltantes.push("{{doctor}}");
      }

      setConfirmacion({
        tipo: "error",
        titulo: "Faltan datos automáticos",
        mensaje:
          `El consentimiento debe conservar ${faltantes.join(
            " y "
          )}. Estos campos son obligatorios para que VexaDent pueda insertar automáticamente los datos del paciente y del odontólogo.`,
        textoBoton: "Entendido",
        accion: () => {
          setConfirmacion(null);
        }
      });

      return;
    }


    setConfirmacion({
      tipo: "guardar",
      titulo: consentimientoEditando
        ? "¿Guardar cambios?"
        : "¿Crear consentimiento?",
      mensaje: consentimientoEditando
        ? "Los cambios realizados se guardarán en este consentimiento personalizado."
        : "Se creará un nuevo consentimiento personalizado para este consultorio.",
      textoBoton: consentimientoEditando
        ? "Sí, guardar cambios"
        : "Sí, crear consentimiento",
      accion: async () => {
        setConfirmacion(null);
        await guardarConsentimiento();
      }
    });
  };

  const cerrarFormularioSinConfirmacion = () => {
    setMostrarFormulario(false);
    setConsentimientoEditando(null);
    setNombreNuevo("");
    setTextoNuevo("");
  };

  const guardarConsentimiento = async () => {
        if (!clinicId) return;

        try {
            const ahora = new Date();

            /*
            * El editor Tiptap genera HTML enriquecido.
            * Lo guardamos en contenidoHtml y mantenemos
            * textoPlantilla para compatibilidad.
            */
            const contenidoHtml = textoNuevo.trim();

            /*
            * EDITAR CONSENTIMIENTO EXISTENTE
            */
            if (consentimientoEditando) {
                const nuevaVersion =
                    (consentimientoEditando.version || 1) + 1;

                await setDoc(
                    doc(
                        db,
                        "clinics",
                        clinicId,
                        "consentimientosConfiguracion",
                        consentimientoEditando.id
                    ),
                    {
                        nombre: nombreNuevo.trim(),
                        textoPlantilla: contenidoHtml,
                        contenidoHtml: contenidoHtml,
                        version: nuevaVersion,
                        fechaActualizacion: ahora
                    },
                    {
                        merge: true
                    }
                );

                setConsentimientos(prev =>
                    prev.map(consentimiento =>
                        consentimiento.id ===
                        consentimientoEditando.id
                            ? {
                                ...consentimiento,
                                nombre: nombreNuevo.trim(),
                                textoPlantilla: contenidoHtml,
                                contenidoHtml: contenidoHtml,
                                version: nuevaVersion
                            }
                            : consentimiento
                    )
                );

                cerrarFormularioSinConfirmacion();
                return;
            }

            /*
            * CREAR NUEVO CONSENTIMIENTO
            */
            const ref = collection(
                db,
                "clinics",
                clinicId,
                "consentimientosConfiguracion"
            );

            const nuevoConsentimiento = {
                nombre: nombreNuevo.trim(),
                textoPlantilla: contenidoHtml,
                contenidoHtml: contenidoHtml,
                activo: true,
                origen: "custom",
                version: 1,
                fechaCreacion: ahora,
                fechaActualizacion: ahora
            };

            const documento = await addDoc(
                ref,
                nuevoConsentimiento
            );

            setConsentimientos(prev => [
                ...prev,
                {
                    id: documento.id,
                    tipo: documento.id,
                    ...nuevoConsentimiento,
                    descripcion:
                        "Consentimiento creado por este consultorio."
                }
            ]);

            cerrarFormularioSinConfirmacion();

        } catch (error) {
            console.error(
                "Error al guardar consentimiento:",
                error
            );

            setConfirmacion({
                tipo: "error",
                titulo: "No se pudo guardar",
                mensaje:
                    "No fue posible guardar el consentimiento. Inténtalo nuevamente.",
                textoBoton: "Entendido",
                accion: () => {
                    setConfirmacion(null);
                }
            });
        }
  };

  const confirmarEliminacion = async () => {
    if (
      !clinicId ||
      !consentimientoAEliminar ||
      consentimientoAEliminar.origen !== "custom"
    ) {
      return;
    }

    try {
      await deleteDoc(
        doc(
          db,
          "clinics",
          clinicId,
          "consentimientosConfiguracion",
          consentimientoAEliminar.id
        )
      );

      setConsentimientos(prev =>
        prev.filter(
          consentimiento =>
            consentimiento.id !==
            consentimientoAEliminar.id
        )
      );

      setConsentimientoAEliminar(null);

    } catch (error) {
      console.error(
        "Error al eliminar consentimiento:",
        error
      );

      setConfirmacion({
        tipo: "error",
        titulo: "No se pudo eliminar",
        mensaje:
          "No fue posible eliminar el consentimiento. Inténtalo nuevamente.",
        textoBoton: "Entendido",
        accion: () => {
          setConfirmacion(null);
        }
      });
    }
  };

  const predeterminados =
    consentimientos.filter(
      consentimiento =>
        consentimiento.origen === "vexadent"
    );

  const personalizados =
    consentimientos.filter(
      consentimiento =>
        consentimiento.origen === "custom"
    );

  const consentimientosActivos = consentimientos.filter(
    consentimiento => consentimiento.activo
  ).length;

  const SwitchEstado = ({ consentimiento }) => (
    <button
      type="button"
      onMouseDown={event => {
        event.preventDefault();
      }}
      onClick={() =>
        cambiarEstado(
          consentimiento.id,
          consentimiento.activo
        )
      }
      aria-label={
        consentimiento.activo
          ? "Desactivar consentimiento"
          : "Activar consentimiento"
      }
      style={{
        position: "relative",
        width: 46,
        height: 26,
        minWidth: 46,
        border: "none",
        borderRadius: 999,
        background: consentimiento.activo
          ? "#2563eb"
          : "#d1d5db",
        cursor: "pointer",
        padding: 0,
        transition:
          "background 0.2s ease"
      }}
    >
      <span
        style={{
          position: "absolute",
          top: 3,
          left: consentimiento.activo
            ? 23
            : 3,
          width: 20,
          height: 20,
          borderRadius: "50%",
          background: "#fff",
          boxShadow:
            "0 1px 4px rgba(0,0,0,0.18)",
          transition:
            "left 0.2s ease"
        }}
      />
    </button>
  );

  const TarjetaConsentimiento = ({
    consentimiento
  }) => (
    <div
      className="vd-settings-module vd-settings-consentimientos"
      style={{
        background: "#fff",
        border:
          "1px solid #e5e7eb",
        borderRadius: 14,
        padding: "16px 18px",
        marginBottom: 10,
        boxShadow:
          "0 1px 3px rgba(15,23,42,0.03)"
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 15
        }}
      >
        <div
          style={{
            minWidth: 0,
            flex: 1
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
              marginBottom: 5
            }}
          >
            <strong
              style={{
                fontSize: 14,
                color: "#111827"
              }}
            >
              {consentimiento.nombre}
            </strong>

            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "3px 7px",
                borderRadius: 999,
                background:
                  consentimiento.origen ===
                  "custom"
                    ? "#eff6ff"
                    : "#f3f4f6",
                color:
                  consentimiento.origen ===
                  "custom"
                    ? "#2563eb"
                    : "#6b7280",
                fontSize: 10,
                fontWeight: 600
              }}
            >
              {consentimiento.origen ===
              "custom"
                ? "Personalizado"
                : "VexaDent"}
            </span>
          </div>

          <div
            style={{
              fontSize: 12,
              lineHeight: 1.5,
              color: "#6b7280"
            }}
          >
            {consentimiento.descripcion}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexShrink: 0
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8
            }}
          >
            <SwitchEstado
              consentimiento={consentimiento}
            />

            <span
              style={{
                minWidth: 48,
                fontSize: 11,
                fontWeight: 600,
                color:
                  consentimiento.activo
                    ? "#2563eb"
                    : "#6b7280"
              }}
            >
              {consentimiento.activo
                ? "Activo"
                : "Inactivo"}
            </span>
          </div>

          {consentimiento.origen ===
            "custom" && (
            <>
              <button
                type="button"
                onClick={() =>
                  abrirEdicion(
                    consentimiento
                  )
                }
                style={{
                  padding:
                    "7px 11px",
                  borderRadius: 8,
                  border:
                    "1px solid #dbeafe",
                  background: "#fff",
                  color: "#2563eb",
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: "pointer"
                }}
              >
                Editar
              </button>

              <button
                type="button"
                onClick={() =>
                  setConsentimientoAEliminar(
                    consentimiento
                  )
                }
                style={{
                  padding:
                    "7px 11px",
                  borderRadius: 8,
                  border:
                    "1px solid #fecaca",
                  background: "#fff",
                  color: "#dc2626",
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: "pointer"
                }}
              >
                Borrar
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div
      style={{
        width: "100%",
        maxWidth: 1180,
        paddingBottom: 40
      }}
    >
      {/* ENCABEZADO */}
      <div
        style={{
          marginBottom: 28
        }}
      >
        <h2
          style={{
            margin: 0,
            marginBottom: 7,
            fontSize: 22,
            fontWeight: 700,
            color: "#111827"
          }}
        >
          Consentimientos informados
        </h2>

        <p
          style={{
            margin: 0,
            color: "#6b7280",
            fontSize: 14,
            lineHeight: 1.5
          }}
        >
          Administra los documentos que tus pacientes
          deberán aceptar antes de determinados
          procedimientos.
        </p>
      </div>

      <div className="vd-settings-module-stats">
        <div><strong>{consentimientosActivos}</strong><span>documentos activos</span></div>
        <div><strong>{predeterminados.length}</strong><span>plantillas incluidas</span></div>
        <div><strong>{personalizados.length}</strong><span>documentos propios</span></div>
      </div>

      {/* COLUMNAS */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "minmax(0, 1fr) minmax(0, 1fr)",
          gap: 24,
          alignItems: "start"
        }}
      >
        {/* COLUMNA IZQUIERDA */}
        <section>
          <div
            style={{
              background: "#f8fafc",
              border:
                "1px solid #e5e7eb",
              borderRadius: 16,
              padding: 18
            }}
          >
            <div
              style={{
                marginBottom: 16
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 5
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#111827"
                  }}
                >
                  Consentimientos de VexaDent
                </h3>

                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: "#6b7280",
                    background: "#e5e7eb",
                    padding:
                      "3px 8px",
                    borderRadius: 999
                  }}
                >
                  Incluidos
                </span>
              </div>

              <p
                style={{
                  margin: 0,
                  fontSize: 12,
                  lineHeight: 1.5,
                  color: "#9ca3af"
                }}
              >
                Estos documentos vienen incluidos
                con VexaDent. Puedes activarlos o
                desactivarlos cuando quieras.
              </p>
            </div>

            {predeterminados.map(
              consentimiento => (
                <TarjetaConsentimiento
                  key={
                    consentimiento.id
                  }
                  consentimiento={
                    consentimiento
                  }
                />
              )
            )}
          </div>
        </section>

        {/* COLUMNA DERECHA */}
        <section>
          <div
            style={{
              background: "#f8fafc",
              border:
                "1px solid #e5e7eb",
              borderRadius: 16,
              padding: 18
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent:
                  "space-between",
                alignItems:
                  "flex-start",
                gap: 15,
                marginBottom: 16
              }}
            >
              <div>
                <div
                  style={{
                    display: "flex",
                    alignItems:
                      "center",
                    gap: 8,
                    marginBottom: 5
                  }}
                >
                  <h3
                    style={{
                      margin: 0,
                      fontSize: 14,
                      fontWeight: 700,
                      color: "#111827"
                    }}
                  >
                    Consentimientos personalizados
                  </h3>

                  {personalizados.length >
                    0 && (
                    <span
                      style={{
                        minWidth: 20,
                        height: 20,
                        padding:
                          "0 6px",
                        display:
                          "inline-flex",
                        alignItems:
                          "center",
                        justifyContent:
                          "center",
                        borderRadius: 999,
                        background:
                          "#eff6ff",
                        color:
                          "#2563eb",
                        fontSize: 10,
                        fontWeight: 700
                      }}
                    >
                      {
                        personalizados.length
                      }
                    </span>
                  )}
                </div>

                <p
                  style={{
                    margin: 0,
                    fontSize: 12,
                    lineHeight: 1.5,
                    color: "#9ca3af"
                  }}
                >
                  Crea documentos específicos para
                  las necesidades de tu consultorio.
                </p>
              </div>

              <button
                type="button"
                onClick={
                  abrirNuevoConsentimiento
                }
                style={{
                  padding:
                    "8px 12px",
                  borderRadius: 9,
                  border: "none",
                  background:
                    "#2563eb",
                  color: "#fff",
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: "pointer",
                  whiteSpace:
                    "nowrap",
                  boxShadow:
                    "0 2px 5px rgba(37,99,235,0.16)"
                }}
              >
                + Nuevo
              </button>
            </div>

            {personalizados.length ===
            0 ? (
              <div
                style={{
                  padding:
                    "38px 20px",
                  borderRadius: 12,
                  border:
                    "1px dashed #d1d5db",
                  background:
                    "#fff",
                  textAlign:
                    "center"
                }}
              >
                <div
                  style={{
                    width: 42,
                    height: 42,
                    margin:
                      "0 auto 12px",
                    borderRadius:
                      "50%",
                    background:
                      "#eff6ff",
                    color:
                      "#2563eb",
                    display:
                      "flex",
                    alignItems:
                      "center",
                    justifyContent:
                      "center",
                    fontSize: 20,
                    fontWeight: 400
                  }}
                >
                  +
                </div>

                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color:
                      "#6b7280",
                    marginBottom: 5
                  }}
                >
                  Aún no tienes
                  consentimientos
                  personalizados
                </div>

                <div
                  style={{
                    fontSize: 11,
                    color:
                      "#9ca3af",
                    lineHeight: 1.5
                  }}
                >
                  Crea uno para agregar un
                  documento propio de tu consultorio.
                </div>
              </div>
            ) : (
              personalizados.map(
                consentimiento => (
                  <TarjetaConsentimiento
                    key={
                      consentimiento.id
                    }
                    consentimiento={
                      consentimiento
                    }
                  />
                )
              )
            )}
          </div>
        </section>
      </div>

      {/* =========================================================
          MODAL CREAR / EDITAR
      ========================================================= */}
      {mostrarFormulario && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background:
              "rgba(15,23,42,0.48)",
            display: "flex",
            alignItems:
              "center",
            justifyContent:
              "center",
            zIndex: 10000,
            padding: 24
          }}
        >
          <div
            style={{
              width: "100%",
            maxWidth: 900,
            height:
                "min(850px, calc(100vh - 32px))",
              background:
                "#fff",
              borderRadius: 18,
              boxShadow:
                "0 24px 60px rgba(0,0,0,0.22)",
              display: "flex",
              flexDirection:
                "column",
              overflow: "hidden"
            }}
          >
            {/* CABECERA FIJA */}
            <div
              style={{
                flexShrink: 0,
                display: "flex",
                justifyContent:
                  "space-between",
                alignItems:
                  "flex-start",
                gap: 20,
                padding:
                  "22px 24px",
                borderBottom:
                  "1px solid #e5e7eb",
                background:
                  "#fff"
              }}
            >
              <div>
                <h3
                  style={{
                    margin: 0,
                    marginBottom: 5,
                    fontSize: 18,
                    fontWeight: 700,
                    color:
                      "#111827"
                  }}
                >
                  {consentimientoEditando
                    ? "Editar consentimiento"
                    : "Nuevo consentimiento"}
                </h3>

                <p
                  style={{
                    margin: 0,
                    fontSize: 12,
                    color:
                      "#6b7280"
                  }}
                >
                  {consentimientoEditando
                    ? "Modifica el contenido de este consentimiento personalizado."
                    : "Crea un consentimiento personalizado para este consultorio."}
                </p>
              </div>

              <button
                type="button"
                onClick={
                  solicitarCancelarFormulario
                }
                style={{
                  width: 34,
                  height: 34,
                  minWidth: 34,
                  borderRadius:
                    "50%",
                  border: "none",
                  background:
                    "#f3f4f6",
                  color:
                    "#6b7280",
                  fontSize: 19,
                  cursor:
                    "pointer",
                  display:
                    "flex",
                  alignItems:
                    "center",
                  justifyContent:
                    "center"
                }}
              >
                ×
              </button>
            </div>

            {/* CONTENIDO CON SCROLL */}
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                padding:
                  "22px 24px"
              }}
            >
              <div
                style={{
                  marginBottom: 18
                }}
              >
                <label
                  style={{
                    display:
                      "block",
                    fontWeight: 600,
                    fontSize: 13,
                    color:
                      "#374151",
                    marginBottom:
                      7
                  }}
                >
                  Nombre del consentimiento
                </label>

                <input
                  type="text"
                  value={
                    nombreNuevo
                  }
                  onChange={e =>
                    setNombreNuevo(
                      e.target.value
                    )
                  }
                  placeholder="Ej. Consentimiento para cirugía periodontal"
                  autoFocus
                  style={{
                    width:
                      "100%",
                    padding:
                      "11px 12px",
                    borderRadius:
                      10,
                    border:
                      "1px solid #d1d5db",
                    boxSizing:
                      "border-box",
                    fontSize: 14,
                    color:
                      "#111827",
                    outline:
                      "none"
                  }}
                />
              </div>

              <div
                    style={{
                      fontWeight: 700,
                      fontSize: 12,
                      marginBottom: 5
                    }}
                  >
                    Datos automáticos
              </div>

              <div>
                <label
                  style={{
                    display:
                      "block",
                    fontWeight: 600,
                    fontSize: 13,
                    color:
                      "#374151",
                    marginBottom:
                      7
                  }}
                >
                  Texto del consentimiento
                </label>

                <div
                    style={{
                        width: "100%",
                        border: "1px solid #d1d5db",
                        borderRadius: 10,
                        background: "#fff",
                        overflow: "hidden"
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 6,
                            padding: 8,
                            borderBottom: "1px solid #e5e7eb",
                            background: "#f9fafb"
                        }}
                    >
                        <button
                            type="button"
                            title="Negrita"
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 34,
                                height: 34,
                                padding: 0,
                                border: editor?.isActive("bold")
                                    ? "1px solid #2563eb"
                                    : "1px solid #d1d5db",
                                borderRadius: 8,
                                background: editor?.isActive("bold") ? "#eff6ff" : "#fff",
                                color: editor?.isActive("bold") ? "#2563eb" : "#374151",
                                cursor: "pointer"
                            }}
                            onClick={() => editor.chain().focus().toggleBold().run()}
                        >
                            <Bold size={18} />
                        </button>

                        <button
                            type="button"
                            title="Cursiva"
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 34,
                                height: 34,
                                padding: 0,
                                border: editor?.isActive("italic")
                                    ? "1px solid #2563eb"
                                    : "1px solid #d1d5db",
                                borderRadius: 8,
                                background: editor?.isActive("italic") ? "#eff6ff" : "#fff",
                                color: editor?.isActive("italic") ? "#2563eb" : "#374151",
                                cursor: "pointer"
                            }}
                            onClick={() => editor.chain().focus().toggleItalic().run()}
                        >
                            <Italic size={18} />
                        </button>

                        <button
                            type="button"
                            title="Subrayado"
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 34,
                                height: 34,
                                padding: 0,
                                border: editor?.isActive("underline")
                                    ? "1px solid #2563eb"
                                    : "1px solid #d1d5db",
                                borderRadius: 8,
                                background: editor?.isActive("underline") ? "#eff6ff" : "#fff",
                                color: editor?.isActive("underline") ? "#2563eb" : "#374151",
                                cursor: "pointer"
                            }}
                            onClick={() => editor.chain().focus().toggleUnderline().run()}
                        >
                            <UnderlineIcon size={18} />
                        </button>

                        <button
                            type="button"
                            title="Tachado"
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 34,
                                height: 34,
                                padding: 0,
                                border: editor?.isActive("strike")
                                    ? "1px solid #2563eb"
                                    : "1px solid #d1d5db",
                                borderRadius: 8,
                                background: editor?.isActive("strike") ? "#eff6ff" : "#fff",
                                color: editor?.isActive("strike") ? "#2563eb" : "#374151",
                                cursor: "pointer"
                            }}
                            onClick={() => editor.chain().focus().toggleStrike().run()}
                        >
                            <Strikethrough size={18} />
                        </button>

                        <select
                            title="Tipo de letra"
                            defaultValue=""
                            style={{
                                height: 34,
                                padding: "0 8px",
                                border: "1px solid #d1d5db",
                                borderRadius: 8,
                                background: "#fff",
                                color: "#374151",
                                cursor: "pointer",
                                fontSize: 14
                            }}
                            onChange={e => {
                                const font = e.target.value;

                                if (!font) {
                                    return;
                                }

                                editor
                                    .chain()
                                    .focus()
                                    .setFontFamily(font)
                                    .run();
                            }}
                        >
                            <option value="">Fuente</option>
                            <option value="Arial">Arial</option>
                            <option value="Times New Roman">Times New Roman</option>
                            <option value="Georgia">Georgia</option>
                            <option value="Verdana">Verdana</option>
                            <option value="Tahoma">Tahoma</option>
                            <option value="Trebuchet MS">Trebuchet MS</option>
                            <option value="Courier New">Courier New</option>
                        </select>

                        <select
                            title="Tamaño de letra"
                            defaultValue=""
                            style={{
                                height: 34,
                                padding: "0 8px",
                                border: "1px solid #d1d5db",
                                borderRadius: 8,
                                background: "#fff",
                                color: "#374151",
                                cursor: "pointer",
                                fontSize: 14
                            }}
                            onChange={e => {
                                const size = e.target.value;

                                if (!size) {
                                    return;
                                }

                                editor
                                    .chain()
                                    .focus()
                                    .setFontSize(size)
                                    .run();
                            }}
                        >
                            <option value="">Tamaño</option>
                            <option value="10px">10</option>
                            <option value="11px">11</option>
                            <option value="12px">12</option>
                            <option value="14px">14</option>
                            <option value="16px">16</option>
                            <option value="18px">18</option>
                            <option value="20px">20</option>
                            <option value="24px">24</option>
                            <option value="28px">28</option>
                            <option value="32px">32</option>
                        </select>

                        <select
                            title="Interlineado"
                            defaultValue=""
                            style={{
                                height: 34,
                                padding: "0 8px",
                                border: "1px solid #d1d5db",
                                borderRadius: 8,
                                background: "#fff",
                                color: "#374151",
                                cursor: "pointer",
                                fontSize: 14
                            }}
                            onChange={e => {
                                const value = e.target.value;

                                if (!value) {
                                    return;
                                }

                                const tipoNodo = editor.isActive("heading")
                                    ? "heading"
                                    : "paragraph";

                                editor
                                    .chain()
                                    .focus()
                                    .updateAttributes(tipoNodo, {
                                        lineHeight: value
                                    })
                                    .run();
                            }}
                        >
                            <option value="">Interlineado</option>
                            <option value="1">Sencillo</option>
                            <option value="1.15">1.15</option>
                            <option value="1.5">1.5</option>
                            <option value="2">Doble</option>
                        </select>

                        <select
                            title="Formato de párrafo"
                            defaultValue="paragraph"
                            style={{
                                height: 34,
                                padding: "0 8px",
                                border: "1px solid #d1d5db",
                                borderRadius: 8,
                                background: "#fff",
                                color: "#374151",
                                cursor: "pointer",
                                fontSize: 14
                            }}
                            onChange={e => {
                                const nivel = e.target.value;

                                if (nivel === "paragraph") {
                                    editor.chain().focus().setParagraph().run();
                                    return;
                                }

                                editor
                                    .chain()
                                    .focus()
                                    .toggleHeading({
                                        level: Number(nivel)
                                    })
                                    .run();
                            }}
                        >
                            <option value="paragraph">Texto normal</option>
                            <option value="1">Título 1</option>
                            <option value="2">Título 2</option>
                            <option value="3">Título 3</option>
                        </select>

                        <label
                            title="Color del texto"
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 34,
                                height: 34,
                                border: "1px solid #d1d5db",
                                borderRadius: 8,
                                background: "#fff",
                                cursor: "pointer",
                                overflow: "hidden"
                            }}
                        >
                            <span
                                style={{
                                    fontSize: 18,
                                    fontWeight: 700,
                                    color: "#2563eb"
                                }}
                            >
                                A
                            </span>

                            <input
                                type="color"
                                title="Elegir color del texto"
                                style={{
                                    position: "absolute",
                                    opacity: 0,
                                    width: 1,
                                    height: 1
                                }}
                                onChange={e => {
                                    editor
                                        .chain()
                                        .focus()
                                        .setColor(e.target.value)
                                        .run();
                                }}
                            />
                        </label>

                        <label
                            title="Color de resaltado"
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 34,
                                height: 34,
                                border: "1px solid #d1d5db",
                                borderRadius: 8,
                                background: "#fff",
                                cursor: "pointer",
                                overflow: "hidden"
                            }}
                        >
                            <span
                                style={{
                                    fontSize: 17,
                                    fontWeight: 700,
                                    background: "#fef08a",
                                    padding: "0 3px",
                                    borderRadius: 2
                                }}
                            >
                                A
                            </span>

                            <input
                                type="color"
                                title="Elegir color de resaltado"
                                style={{
                                    position: "absolute",
                                    opacity: 0,
                                    width: 1,
                                    height: 1
                                }}
                                onChange={e => {
                                    editor
                                        .chain()
                                        .focus()
                                        .toggleHighlight({ color: e.target.value })
                                        .run();
                                }}
                            />
                        </label>

                        <button
                            type="button"
                            title="Quitar formato de texto"
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 34,
                                height: 34,
                                padding: 0,
                                border: "1px solid #d1d5db",
                                borderRadius: 8,
                                background: "#fff",
                                color: "#374151",
                                cursor: "pointer"
                            }}
                            onClick={() => {
                                editor
                                    .chain()
                                    .focus()
                                    .unsetColor()
                                    .unsetHighlight()
                                    .run();
                            }}
                        >
                            <Eraser size={18} />
                        </button>

                        <button
                            type="button"
                            title="Alinear a la izquierda"
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 34,
                                height: 34,
                                padding: 0,
                                border: editor?.isActive({ textAlign: "left" })
                                    ? "1px solid #2563eb"
                                    : "1px solid #d1d5db",
                                borderRadius: 8,
                                background: editor?.isActive({ textAlign: "left" })
                                    ? "#eff6ff"
                                    : "#fff",
                                color: editor?.isActive({ textAlign: "left" })
                                    ? "#2563eb"
                                    : "#374151",
                                cursor: "pointer"
                            }}
                            onClick={() => editor.chain().focus().setTextAlign("left").run()}
                        >
                            <AlignLeft size={18} />
                        </button>

                        <button
                            type="button"
                            title="Centrar"
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 34,
                                height: 34,
                                padding: 0,
                                border: editor?.isActive({ textAlign: "center" })
                                    ? "1px solid #2563eb"
                                    : "1px solid #d1d5db",
                                borderRadius: 8,
                                background: editor?.isActive({ textAlign: "center" })
                                    ? "#eff6ff"
                                    : "#fff",
                                color: editor?.isActive({ textAlign: "center" })
                                    ? "#2563eb"
                                    : "#374151",
                                cursor: "pointer"
                            }}
                            onClick={() => editor.chain().focus().setTextAlign("center").run()}
                        >
                            <AlignCenter size={18} />
                        </button>

                        <button
                            type="button"
                            title="Alinear a la derecha"
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 34,
                                height: 34,
                                padding: 0,
                                border: editor?.isActive({ textAlign: "right" })
                                    ? "1px solid #2563eb"
                                    : "1px solid #d1d5db",
                                borderRadius: 8,
                                background: editor?.isActive({ textAlign: "right" })
                                    ? "#eff6ff"
                                    : "#fff",
                                color: editor?.isActive({ textAlign: "right" })
                                    ? "#2563eb"
                                    : "#374151",
                                cursor: "pointer"
                            }}
                            onClick={() => editor.chain().focus().setTextAlign("right").run()}
                        >
                            <AlignRight size={18} />
                        </button>

                        <button
                            type="button"
                            title="Justificar"
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 34,
                                height: 34,
                                padding: 0,
                                border: editor?.isActive({ textAlign: "justify" })
                                    ? "1px solid #2563eb"
                                    : "1px solid #d1d5db",
                                borderRadius: 8,
                                background: editor?.isActive({ textAlign: "justify" })
                                    ? "#eff6ff"
                                    : "#fff",
                                color: editor?.isActive({ textAlign: "justify" })
                                    ? "#2563eb"
                                    : "#374151",
                                cursor: "pointer"
                            }}
                            onClick={() => editor.chain().focus().setTextAlign("justify").run()}
                        >
                            <AlignJustify size={18} />
                        </button>

                        <button
                            type="button"
                            title="Lista con viñetas"
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 34,
                                height: 34,
                                padding: 0,
                                border: editor?.isActive("bulletList")
                                    ? "1px solid #2563eb"
                                    : "1px solid #d1d5db",
                                borderRadius: 8,
                                background: editor?.isActive("bulletList")
                                    ? "#eff6ff"
                                    : "#fff",
                                color: editor?.isActive("bulletList")
                                    ? "#2563eb"
                                    : "#374151",
                                cursor: "pointer"
                            }}
                            onClick={() => editor.chain().focus().toggleBulletList().run()}
                        >
                            <List size={18} />
                        </button>

                        <button
                            type="button"
                            title="Lista numerada"
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 34,
                                height: 34,
                                padding: 0,
                                border: editor?.isActive("orderedList")
                                    ? "1px solid #2563eb"
                                    : "1px solid #d1d5db",
                                borderRadius: 8,
                                background: editor?.isActive("orderedList")
                                    ? "#eff6ff"
                                    : "#fff",
                                color: editor?.isActive("orderedList")
                                    ? "#2563eb"
                                    : "#374151",
                                cursor: "pointer"
                            }}
                            onClick={() => editor.chain().focus().toggleOrderedList().run()}
                        >
                            <ListOrdered size={18} />
                        </button>

                        <button
                            type="button"
                            title="Aumentar sangría"
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 34,
                                height: 34,
                                padding: 0,
                                border: "1px solid #d1d5db",
                                borderRadius: 8,
                                background: "#fff",
                                color: "#374151",
                                cursor: "pointer"
                            }}
                            onClick={() => {
                                editor
                                    .chain()
                                    .focus()
                                    .sinkListItem("listItem")
                                    .run();
                            }}
                        >
                            <IndentIncrease size={18} />
                        </button>

                        <button
                            type="button"
                            title="Disminuir sangría"
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 34,
                                height: 34,
                                padding: 0,
                                border: "1px solid #d1d5db",
                                borderRadius: 8,
                                background: "#fff",
                                color: "#374151",
                                cursor: "pointer"
                            }}
                            onClick={() => {
                                editor
                                    .chain()
                                    .focus()
                                    .liftListItem("listItem")
                                    .run();
                            }}
                        >
                            <IndentDecrease size={18} />
                        </button>

                        <button
                            type="button"
                            title="Insertar línea divisoria"
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 34,
                                height: 34,
                                padding: 0,
                                border: "1px solid #d1d5db",
                                borderRadius: 8,
                                background: "#fff",
                                color: "#374151",
                                cursor: "pointer"
                            }}
                            onClick={() => {
                                editor
                                    .chain()
                                    .focus()
                                    .setHorizontalRule()
                                    .run();
                            }}
                        >
                            <Minus size={18} />
                        </button>

                        <button
                            type="button"
                            title="Insertar tabla"
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 34,
                                height: 34,
                                padding: 0,
                                border: "1px solid #d1d5db",
                                borderRadius: 8,
                                background: "#fff",
                                color: "#374151",
                                cursor: "pointer"
                            }}
                            onClick={() => {
                                editor
                                    .chain()
                                    .focus()
                                    .insertTable({
                                        rows: 3,
                                        cols: 3,
                                        withHeaderRow: true
                                    })
                                    .run();
                            }}
                        >
                            <Table2 size={18} />
                        </button>

                        <button
                            type="button"
                            title="Agregar fila"
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 34,
                                height: 34,
                                padding: 0,
                                border: "1px solid #d1d5db",
                                borderRadius: 8,
                                background: "#fff",
                                color: "#374151",
                                cursor: "pointer"
                            }}
                            onClick={() => {
                                editor
                                    .chain()
                                    .focus()
                                    .addRowAfter()
                                    .run();
                            }}
                        >
                            <Table2 size={18} />
                        </button>

                        <button
                            type="button"
                            title="Agregar columna"
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 34,
                                height: 34,
                                padding: 0,
                                border: "1px solid #d1d5db",
                                borderRadius: 8,
                                background: "#fff",
                                color: "#374151",
                                cursor: "pointer"
                            }}
                            onClick={() => {
                                editor
                                    .chain()
                                    .focus()
                                    .addColumnAfter()
                                    .run();
                            }}
                        >
                            <Table2 size={18} />
                        </button>

                        <button
                            type="button"
                            title="Eliminar fila"
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 34,
                                height: 34,
                                padding: 0,
                                border: "1px solid #d1d5db",
                                borderRadius: 8,
                                background: "#fff",
                                color: "#374151",
                                cursor: "pointer"
                            }}
                            onClick={() => {
                                editor
                                    .chain()
                                    .focus()
                                    .deleteRow()
                                    .run();
                            }}
                        >
                            <Trash2 size={18} />
                        </button>

                        <button
                            type="button"
                            title="Eliminar columna"
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 34,
                                height: 34,
                                padding: 0,
                                border: "1px solid #d1d5db",
                                borderRadius: 8,
                                background: "#fff",
                                color: "#374151",
                                cursor: "pointer"
                            }}
                            onClick={() => {
                                editor
                                    .chain()
                                    .focus()
                                    .deleteColumn()
                                    .run();
                            }}
                        >
                            <Trash2 size={18} />
                        </button>

                        <button
                            type="button"
                            title="Eliminar tabla"
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 34,
                                height: 34,
                                padding: 0,
                                border: "1px solid #d1d5db",
                                borderRadius: 8,
                                background: "#fff",
                                color: "#dc2626",
                                cursor: "pointer"
                            }}
                            onClick={() => {
                                editor
                                    .chain()
                                    .focus()
                                    .deleteTable()
                                    .run();
                            }}
                        >
                            <Trash2 size={18} />
                        </button>

                        <button
                            type="button"
                            title="Deshacer"
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 34,
                                height: 34,
                                padding: 0,
                                border: "1px solid #d1d5db",
                                borderRadius: 8,
                                background: "#fff",
                                color: "#374151",
                                cursor: "pointer"
                            }}
                            onClick={() => editor.chain().focus().undo().run()}
                        >
                            <Undo2 size={18} />
                        </button>

                        <button
                            type="button"
                            title="Rehacer"
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 34,
                                height: 34,
                                padding: 0,
                                border: "1px solid #d1d5db",
                                borderRadius: 8,
                                background: "#fff",
                                color: "#374151",
                                cursor: "pointer"
                            }}
                            onClick={() => editor.chain().focus().redo().run()}
                        >
                            <Redo2 size={18} />
                        </button>
                    </div>

                    <style>
                        {`
                            .consentimiento-editor .ProseMirror {
                                min-height: 350px;
                                padding: 18px;
                                outline: none;
                                font-family: Arial, sans-serif;
                                font-size: 14px;
                                line-height: 1.5;
                            }

                            .consentimiento-editor .ProseMirror table {
                                width: 100%;
                                border-collapse: collapse;
                                margin: 12px 0;
                                table-layout: fixed;
                            }

                            .consentimiento-editor .ProseMirror th,
                            .consentimiento-editor .ProseMirror td {
                                border: 1px solid #9ca3af;
                                padding: 8px;
                                min-width: 80px;
                                vertical-align: top;
                                text-align: left;
                            }

                            .consentimiento-editor .ProseMirror th {
                                background: #f3f4f6;
                                font-weight: 600;
                            }

                            .consentimiento-editor .ProseMirror .selectedCell {
                                background: #dbeafe;
                            }

                            .consentimiento-editor .ProseMirror hr {
                                border: none;
                                border-top: 1px solid #9ca3af;
                                margin: 18px 0;
                            }

                            .consentimiento-editor .ProseMirror ul {
                                list-style: disc;
                                padding-left: 32px;
                                margin: 8px 0;
                            }

                            .consentimiento-editor .ProseMirror ol {
                                list-style: decimal;
                                padding-left: 32px;
                                margin: 8px 0;
                            }

                            .consentimiento-editor .ProseMirror ul li {
                                list-style-type: disc;
                                display: list-item;
                                margin: 3px 0;
                            }

                            .consentimiento-editor .ProseMirror ol li {
                                list-style-type: decimal;
                                display: list-item;
                                margin: 3px 0;
                            }

                            .consentimiento-editor .ProseMirror ul li::marker {
                                content: "• ";
                            }

                            .consentimiento-editor .ProseMirror ol li::marker {
                                content: counter(list-item) ". ";
                            }

                            .consentimiento-editor .ProseMirror li > p {
                                margin: 0;
                            }

                            .consentimiento-editor .ProseMirror h1 {
                                font-size: 28px;
                                margin: 18px 0 10px;
                            }

                            .consentimiento-editor .ProseMirror h2 {
                                font-size: 22px;
                                margin: 16px 0 8px;
                            }

                            .consentimiento-editor .ProseMirror h3 {
                                font-size: 18px;
                                margin: 14px 0 6px;
                            }
                        `}
                    </style>

                    <div className="consentimiento-editor">
                        <EditorContent editor={editor} />
                    </div>
                </div>

                <div
                  style={{
                    marginTop: 10,
                    padding: "12px 14px",
                    borderRadius: 10,
                    background: "#eff6ff",
                    border: "1px solid #bfdbfe",
                    color: "#1e3a8a"
                  }}
                >

                  <div
                    style={{
                      fontSize: 11,
                      lineHeight: 1.6,
                      marginBottom: 10
                    }}
                  >
                    Los campos{" "}
                    <code>{"{{paciente}}"}</code> y{" "}
                    <code>{"{{doctor}}"}</code> se
                    sustituirán automáticamente con los
                    datos del paciente y del odontólogo.
                    <strong>
                      {" "}
                      Ambos deben permanecer en el
                      consentimiento.
                    </strong>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      flexWrap: "wrap"
                    }}
                  >
                    <button
                      type="button"
                      onClick={() =>
                        insertarVariable(
                          "{{paciente}}"
                        )
                      }
                      style={{
                        border:
                          "1px solid #bfdbfe",
                        borderRadius: 8,
                        padding:
                          "7px 10px",
                        background: "#fff",
                        color: "#1d4ed8",
                        cursor: "pointer",
                        fontWeight: 600,
                        fontSize: 11
                      }}
                    >
                      + Insertar paciente
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        insertarVariable(
                          "{{doctor}}"
                        )
                      }
                      style={{
                        border:
                          "1px solid #bfdbfe",
                        borderRadius: 8,
                        padding:
                          "7px 10px",
                        background: "#fff",
                        color: "#1d4ed8",
                        cursor: "pointer",
                        fontWeight: 600,
                        fontSize: 11
                      }}
                    >
                      + Insertar odontólogo
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* FOOTER FIJO */}
            <div
              style={{
                flexShrink: 0,
                display:
                  "flex",
                justifyContent:
                  "flex-end",
                gap: 10,
                padding:
                  "16px 24px",
                borderTop:
                  "1px solid #e5e7eb",
                background:
                  "#fafafa",
                boxShadow:
                  "0 -3px 10px rgba(15,23,42,0.03)"
              }}
            >
              <button
                type="button"
                onClick={
                  solicitarCancelarFormulario
                }
                style={{
                  padding:
                    "10px 16px",
                  borderRadius:
                    10,
                  border:
                    "1px solid #d1d5db",
                  background:
                    "#fff",
                  color:
                    "#374151",
                  cursor:
                    "pointer",
                  fontWeight: 600,
                  fontSize: 13
                }}
              >
                Cancelar
              </button>

              <button
                type="button"
                onClick={
                  solicitarGuardarConsentimiento
                }
                style={{
                  padding:
                    "10px 18px",
                  borderRadius:
                    10,
                  border: "none",
                  background:
                    "#2563eb",
                  color:
                    "#fff",
                  cursor:
                    "pointer",
                  fontWeight: 600,
                  fontSize: 13,
                  boxShadow:
                    "0 2px 5px rgba(37,99,235,0.18)"
                }}
              >
                {consentimientoEditando
                  ? "Guardar cambios"
                  : "Crear consentimiento"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================
          MODAL CONFIRMACIONES
      ========================================================= */}
      {confirmacion && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background:
              "rgba(15,23,42,0.55)",
            display: "flex",
            alignItems:
              "center",
            justifyContent:
              "center",
            zIndex: 12000,
            padding: 20
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 440,
              background:
                "#fff",
              borderRadius: 18,
              padding: 28,
              boxShadow:
                "0 24px 60px rgba(0,0,0,0.24)"
            }}
          >
            <div
              style={{
                width: 46,
                height: 46,
                borderRadius:
                  "50%",
                background:
                  confirmacion.tipo ===
                  "error"
                    ? "#fee2e2"
                    : "#eff6ff",
                color:
                  confirmacion.tipo ===
                  "error"
                    ? "#dc2626"
                    : "#2563eb",
                display:
                  "flex",
                alignItems:
                  "center",
                justifyContent:
                  "center",
                fontSize: 20,
                fontWeight: 700,
                marginBottom:
                  16
              }}
            >
              {confirmacion.tipo ===
              "error"
                ? "!"
                : "✓"}
            </div>

            <h3
              style={{
                margin: 0,
                marginBottom:
                  10,
                fontSize: 19,
                color:
                  "#111827"
              }}
            >
              {confirmacion.titulo}
            </h3>

            <p
              style={{
                margin: 0,
                color:
                  "#6b7280",
                fontSize: 14,
                lineHeight:
                  1.6
              }}
            >
              {confirmacion.mensaje}
            </p>

            <div
              style={{
                display:
                  "flex",
                justifyContent:
                  "flex-end",
                gap: 10,
                marginTop:
                  24
              }}
            >
              {confirmacion.tipo !==
                "error" && (
                <button
                  type="button"
                  onClick={() =>
                    setConfirmacion(
                      null
                    )
                  }
                  style={{
                    padding:
                      "10px 16px",
                    borderRadius:
                      10,
                    border:
                      "1px solid #d1d5db",
                    background:
                      "#fff",
                    color:
                      "#374151",
                    cursor:
                      "pointer",
                    fontWeight: 600,
                    fontSize: 13
                  }}
                >
                  Volver
                </button>
              )}

              <button
                type="button"
                onClick={
                  confirmacion.accion
                }
                style={{
                  padding:
                    "10px 16px",
                  borderRadius:
                    10,
                  border: "none",
                  background:
                    confirmacion.tipo ===
                    "error"
                      ? "#2563eb"
                      : "#2563eb",
                  color:
                    "#fff",
                  cursor:
                    "pointer",
                  fontWeight: 600,
                  fontSize: 13
                }}
              >
                {
                  confirmacion.textoBoton
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================
          MODAL DE ELIMINACIÓN
      ========================================================= */}
      {consentimientoAEliminar && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background:
              "rgba(15,23,42,0.5)",
            display: "flex",
            alignItems:
              "center",
            justifyContent:
              "center",
            zIndex: 11000,
            padding: 20
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 440,
              background:
                "#fff",
              borderRadius: 18,
              padding: 28,
              boxShadow:
                "0 24px 60px rgba(0,0,0,0.22)"
            }}
          >
            <div
              style={{
                width: 46,
                height: 46,
                borderRadius:
                  "50%",
                background:
                  "#fee2e2",
                color:
                  "#dc2626",
                display:
                  "flex",
                alignItems:
                  "center",
                justifyContent:
                  "center",
                fontSize: 22,
                fontWeight: 700,
                marginBottom:
                  16
              }}
            >
              !
            </div>

            <h3
              style={{
                margin: 0,
                marginBottom:
                  10,
                fontSize: 20,
                color:
                  "#111827"
              }}
            >
              ¿Borrar consentimiento?
            </h3>

            <p
              style={{
                color:
                  "#6b7280",
                lineHeight:
                  1.6,
                marginTop: 0,
                marginBottom:
                  10,
                fontSize: 14
              }}
            >
              Estás a punto de eliminar
              permanentemente este
              consentimiento:
            </p>

            <div
              style={{
                padding:
                  "12px 14px",
                borderRadius:
                  10,
                background:
                  "#f3f4f6",
                marginBottom:
                  16,
                fontWeight: 700,
                fontSize: 14,
                color:
                  "#111827"
              }}
            >
              {
                consentimientoAEliminar.nombre
              }
            </div>

            <p
              style={{
                color:
                  "#6b7280",
                lineHeight:
                  1.6,
                margin: 0,
                fontSize: 13
              }}
            >
              Esta información se eliminará
              permanentemente. No existe recuperación
              y esta acción no se puede deshacer.
            </p>

            <div
              style={{
                display:
                  "flex",
                justifyContent:
                  "flex-end",
                gap: 10,
                marginTop:
                  24
              }}
            >
              <button
                type="button"
                onClick={() =>
                  setConsentimientoAEliminar(
                    null
                  )
                }
                style={{
                  padding:
                    "10px 16px",
                  borderRadius:
                    10,
                  border:
                    "1px solid #d1d5db",
                  background:
                    "#fff",
                  color:
                    "#374151",
                  cursor:
                    "pointer",
                  fontWeight: 600,
                  fontSize: 13
                }}
              >
                Cancelar
              </button>

              <button
                type="button"
                onClick={
                  confirmarEliminacion
                }
                style={{
                  padding:
                    "10px 16px",
                  borderRadius:
                    10,
                  border: "none",
                  background:
                    "#dc2626",
                  color:
                    "#fff",
                  cursor:
                    "pointer",
                  fontWeight: 600,
                  fontSize: 13
                }}
              >
                Sí, borrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
